import { AxiosResponse } from "axios";
import apiService from "services/apiService";

// ─── Types ──────────────────────────────────────────────────────────────────

// An admin candidate returned by GET /admin-emails. The UI shows `displayName`
// while the email is what actually gets persisted as a CC recipient.
export interface AdminEmail {
  userId: number;
  displayName: string;
  email: string;
}

// A configured CC recipient as returned by GET /leave-mail-cc-config. The `id`
// is the server row id — required to delete a single recipient.
export interface Recipient {
  id: number;
  email: string;
  displayName?: string;
}

// The GET /leave-mail-cc-config response shape can vary by backend
// (bare string[], { emails: [...] }, [{ email }], or [{ id, email, displayName }]).
// `normalizeRecipients` flattens to emails; `normalizeRecipientList` preserves ids.
type RecipientsResponse = string[] | { emails: string[] } | Recipient[] | AdminEmail[];

const LEAVE_MAIL_CC_ENDPOINT = "api/leave-mail-cc-config";

class LeaveMailCcService {
  // List of admins the current tenant can pick as CC recipients.
  getAdminEmails(): Promise<AxiosResponse<AdminEmail[]>> {
    return apiService.get(`${LEAVE_MAIL_CC_ENDPOINT}/admin-emails`);
  }

  // Currently configured CC recipients.
  getRecipients(): Promise<AxiosResponse<RecipientsResponse>> {
    return apiService.get(LEAVE_MAIL_CC_ENDPOINT);
  }

  // Replace the CC recipient list. Backend expects { emails: [...] }.
  saveRecipients(emails: string[]): Promise<AxiosResponse<RecipientsResponse>> {
    return apiService.post(LEAVE_MAIL_CC_ENDPOINT, { emails });
  }

  // Remove a single CC recipient by its server id.
  deleteRecipient(id: number): Promise<AxiosResponse<void>> {
    return apiService.delete(`${LEAVE_MAIL_CC_ENDPOINT}/${id}`);
  }
}

// Flatten any of the supported response shapes into a de-duplicated email list.
// Used by the config listing page for the "N active" count badge.
export const normalizeRecipients = (data: RecipientsResponse | undefined | null): string[] => {
  if (!data) return [];
  const raw = Array.isArray(data) ? data : Array.isArray(data.emails) ? data.emails : [];
  const emails = raw
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((item: any) => (typeof item === "string" ? item : item?.email))
    .filter((email: unknown): email is string => typeof email === "string" && email.length > 0);
  return Array.from(new Set(emails));
};

// Flatten to a Recipient[] that preserves the server id (needed for delete).
// Only object entries carrying a numeric id + email are kept, since a recipient
// without an id cannot be deleted individually.
export const normalizeRecipientList = (
  data: RecipientsResponse | undefined | null
): Recipient[] => {
  if (!data) return [];
  const raw = Array.isArray(data) ? data : Array.isArray(data.emails) ? data.emails : [];
  return (
    raw
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((item: any): Recipient | null =>
        item && typeof item.id === "number" && typeof item.email === "string" && item.email
          ? { id: item.id, email: item.email, displayName: item.displayName }
          : null
      )
      .filter((r): r is Recipient => r !== null)
  );
};

const leaveMailCcService = new LeaveMailCcService();
export default leaveMailCcService;
